// components/Contact.tsx
import { useState } from "react";
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaClock,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaPaperPlane,
  FaCheckCircle
} from "react-icons/fa";
import { ScrollToTop } from "../components";

/**
 * Contact page component with contact information, form, and map.
 * 
 * Features:
 * - Contact information cards with icons
 * - Contact form with validation
 * - Social media links
 * - Embedded map
 * - Responsive design
 * - Success message on form submission
 * 
 * @returns {JSX.Element} The contact page
 */
export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    // In production, send to API:
    // await fetch('/api/contact', { method: 'POST', body: JSON.stringify(formData) });
    
    setIsLoading(false);
    setIsSubmitted(true);
    setFormData({ name: "", email: "", subject: "", message: "" });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-2xl" />,
      title: "Address",
      details: ["123 Luxury Street", "Beverly Hills, CA 90210", "United States"],
      color: "blue",
    },
    {
      icon: <FaPhone className="text-2xl" />,
      title: "Phone",
      details: ["+1 (555) 123-4567", "+1 (555) 987-6543"],
      color: "green",
    },
    {
      icon: <FaEnvelope className="text-2xl" />,
      title: "Email",
      details: ["info@hoteladina.com", "reservations@hoteladina.com"],
      color: "purple",
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: "Working Hours",
      details: ["24/7 Guest Support", "Check-in: 3:00 PM", "Check-out: 11:00 AM"],
      color: "orange",
    },
  ];

  const socialLinks = [
    { icon: <FaFacebook />, url: "https://facebook.com", label: "Facebook" },
    { icon: <FaInstagram />, url: "https://instagram.com", label: "Instagram" },
    { icon: <FaTwitter />, url: "https://twitter.com", label: "Twitter" },
    { icon: <FaYoutube />, url: "https://youtube.com", label: "YouTube" },
  ];

  return (
    <section className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <ScrollToTop />
      
      {/* Hero Section */}
      <div className="relative h-[300px] md:h-[400px] flex justify-center items-center bg-cover bg-center" 
           style={{ backgroundImage: "url('https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200')" }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/70" />
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-primary font-bold mb-4">
            Contact Us
          </h1>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-white/90 font-secondary text-lg max-w-2xl mx-auto">
            We'd love to hear from you. Get in touch with us for any inquiries or reservations.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="container mx-auto max-w-7xl px-4 -mt-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className={`text-${info.color}-600 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                {info.icon}
              </div>
              <h3 className="font-primary text-lg font-bold text-gray-800 mb-2">
                {info.title}
              </h3>
              <div className="space-y-1">
                {info.details.map((detail, idx) => (
                  <p key={idx} className="font-secondary text-sm text-gray-600">
                    {detail}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Form & Map */}
      <div className="container mx-auto max-w-7xl px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="mb-8">
              <h2 className="font-primary text-3xl font-bold text-gray-800 mb-2">
                Send Us a Message
              </h2>
              <div className="w-16 h-1 bg-blue-600 mb-4"></div>
              <p className="font-secondary text-gray-600">
                Have questions or special requests? We're here to help!
              </p>
            </div>

            {isSubmitted && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                <FaCheckCircle className="text-green-600 text-xl" />
                <p className="font-secondary text-green-700">
                  Thank you! Your message has been sent successfully.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block font-secondary text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 outline-none font-secondary"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block font-secondary text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 outline-none font-secondary"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block font-secondary text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 outline-none font-secondary"
                  placeholder="Room Reservation Inquiry"
                />
              </div>

              <div>
                <label htmlFor="message" className="block font-secondary text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 outline-none font-secondary resize-none"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Send Message
                  </>
                )}
              </button>
            </form>

            {/* Social Links */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="font-secondary text-sm text-gray-600 mb-4 text-center">
                Connect with us on social media
              </p>
              <div className="flex justify-center gap-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-12 h-12 bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white rounded-full flex items-center justify-center text-xl transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Map & Additional Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="font-primary text-xl font-bold text-gray-800">
                  Find Us Here
                </h3>
                <div className="w-16 h-1 bg-blue-600 mt-2"></div>
              </div>
              <div className="h-[300px] w-full bg-gray-200">
                {/* Replace with actual Google Maps embed */}
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.431768456473!2d-118.40068468478276!3d34.13430618058314!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2b93b3e5b3c6d%3A0x2e4d3e9c2e4d3e9c!2sBeverly%20Hills%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Hotel Location Map"
                />
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="font-primary text-xl font-bold mb-4">
                Need Immediate Help?
              </h3>
              <p className="font-secondary text-white/90 mb-4">
                Our dedicated team is available 24/7 to assist you with any inquiries or reservations.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <FaPhone className="text-white/80" />
                  <span className="font-secondary font-medium">+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <FaEnvelope className="text-white/80" />
                  <span className="font-secondary font-medium">info@hoteladina.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}